import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;

const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 30px 0; // Add margin-bottom here
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;

const StyledCaption = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  font-size: 2em;
  color: white;
`;

const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  &:hover ${StyledCaption} {
    opacity: 1;
  }

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'Design Thinking',
    'User Research',
    'Requirements Gathering',
    'Miro',
    'Figma',
    'Usability Testing',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2>About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              My journey into understanding people and systems began in client-facing roles within
              the printing and service industries, where I developed an sense for real-world needs.
              This led me to a deep fascination with Human-Computer Interaction, and how truly
              human-centered approaches can shape our digital experiences.
              <p>
                Now, I’m focused on exploring how user-centric design and service strategy can
                converge to create thoughtful, innovative, and sustainable systems{' '}
              </p>
            </p>
            <p>Here are a few tools and processes I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
          <p>
            Beyond research and design, I freelance as a video editor and copywriter, and volunteer
            as a proofreader for{' '}
            <a href="https://www.getdailyart.com/" target="_blank" rel="noopener noreferrer">
              DailyArt
            </a>
            {''}. When not online, I'm usually reading, staying active outdoors or at the gym, or
            enjoying a lovely garden 🏡.
          </p>

          <p>
            By the way, add me on{' '}
            <a href="https://www.last.fm/user/eisteis" target="_blank" rel="noopener noreferrer">
              Last.fm
            </a>{' '}
            :)
          </p>
        </StyledText>

        <StyledPic>
          {/* <StyledCaption>This is .me</StyledCaption> */}
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
